import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import Cookies from "js-cookie";
import { useAppStore } from "@stores/appStore";

/**
 * Cookie names for authentication
 */
export const AUTH_COOKIES = {
  ACCESS_TOKEN: "access-token",
  REFRESH_TOKEN: "refresh-token",
  TOKEN_TYPE: "token-type",
  LOGIN_CSRF: "login-csrf",
} as const;

/**
 * Cookie options
 */
export const COOKIE_OPTIONS = {
  sameSite: "Strict" as const,
  secure: import.meta.env.VITE_SECURE_COOKIES === "true",
};

/**
 * API base URL from environment
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || "";

/**
 * Create axios instance with default config
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Get auth header from cookies
 */
function getAuthHeader(): string | null {
  const accessToken = Cookies.get(AUTH_COOKIES.ACCESS_TOKEN);
  const tokenType = Cookies.get(AUTH_COOKIES.TOKEN_TYPE);

  if (accessToken && tokenType) {
    return `${tokenType} ${accessToken}`;
  }
  return null;
}

/**
 * Request interceptor - adds auth header
 */
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Don't add auth header for token refresh endpoints
    if (config.url && !config.url.endsWith("token")) {
      const authHeader = getAuthHeader();
      if (authHeader) {
        config.headers.Authorization = authHeader;
      }
    }

    // Add zoneId header from selected production line
    const state = useAppStore.getState();
    const timezone = state.selectedProductionLine?.timeZone;
    if (timezone) {
      config.headers['zoneId'] = timezone;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor - handles auth errors
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - redirect to login
      handleAuthError();
    }
    return Promise.reject(error);
  }
);

/**
 * Handle authentication errors
 */
export function handleAuthError(): void {
  // Clear auth cookies
  Cookies.remove(AUTH_COOKIES.ACCESS_TOKEN);
  Cookies.remove(AUTH_COOKIES.REFRESH_TOKEN);
  Cookies.remove(AUTH_COOKIES.TOKEN_TYPE);
  Cookies.remove(AUTH_COOKIES.LOGIN_CSRF);

  // Redirect to signed out page
  const signedOutUrl = import.meta.env.VITE_SIGNED_OUT_URL || "/signed-out";
  window.location.replace(signedOutUrl);
}

/**
 * Store access token in cookies
 */
export function storeAccessToken(
  accessToken: string,
  refreshToken: string,
  tokenType = "Bearer"
): void {
  Cookies.set(AUTH_COOKIES.ACCESS_TOKEN, accessToken, COOKIE_OPTIONS);
  Cookies.set(AUTH_COOKIES.REFRESH_TOKEN, refreshToken, COOKIE_OPTIONS);
  Cookies.set(AUTH_COOKIES.TOKEN_TYPE, tokenType, COOKIE_OPTIONS);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!Cookies.get(AUTH_COOKIES.ACCESS_TOKEN);
}

/**
 * Clear all auth cookies
 */
export function clearAuth(): void {
  Cookies.remove(AUTH_COOKIES.ACCESS_TOKEN);
  Cookies.remove(AUTH_COOKIES.REFRESH_TOKEN);
  Cookies.remove(AUTH_COOKIES.TOKEN_TYPE);
  Cookies.remove(AUTH_COOKIES.LOGIN_CSRF);
}

export default apiClient;
